import { updateConfigurationProductsLocally } from '@/hooks/utils/updateConfigurationProductsLocally';
import { useRouter } from '@/i18n/routing';
import { useOfferToolUser } from '@/providers/OfferToolUserProvider/OfferToolUserProvider';
import { api } from '@/trpc/client';
import { useIsMutating } from '@tanstack/react-query';
import { getMutationKey } from '@trpc/react-query';

export const useConfigurationApi = () => {
  const router = useRouter();
  const utils = api.useUtils();
  const { user } = useOfferToolUser();
  const getById = api.configuration.getById.useQuery;
  const getAssistantTopics = api.configuration.getAssistantTopics.useQuery;

  const previewConfig = api.configuration.previewConfig.useMutation({
    onSuccess: () => {
      void utils.site.getConfigurationsById.invalidate();
    },
  });

  const deleteConfiguration = api.configuration.delete.useMutation({
    onSuccess: () => {
      void utils.site.getConfigurationsById.invalidate();
    },
  });

  const duplicateConfiguration = api.configuration.duplicate.useMutation({
    onSuccess: () => {
      void utils.configuration.getById.invalidate();
      void utils.site.getConfigurationsById.invalidate();
      router.refresh();
    },
  });

  const addConceptProductsToConfiguration =
    api.configuration.addConceptProductsToConfiguration.useMutation({
      onSuccess: () => {
        void utils.configuration.getById.invalidate();
        router.refresh();
      },
    });

  const updateConfig = api.configuration.update.useMutation({
    onSuccess: (_data, variables) => {
      void utils.configuration.getById.invalidate({
        configurationHashId: variables.configurationId,
      });
      router.refresh();
    },
  });

  const updateConfigWithoutInvalidation =
    api.configuration.update.useMutation();

  const updateConfigOptimistic = api.configuration.update.useMutation({
    onMutate: async ({
      configurationId: configurationHashId,
      calculationValues,
    }) => {
      await utils.configuration.getById.cancel(
        {
          configurationHashId,
          userId: user?.id,
        },
        { silent: true },
      );

      const householdKwh =
        calculationValues?.consumption?.household?.amountKwhPerYear;
      const heatKwh = calculationValues?.consumption?.heat?.amountKwhPerYear;
      const spotPriceEurPerKwh = calculationValues?.spotPriceEurPerKwh;
      const existingSystemPvProductionKwhPerYear =
        calculationValues?.existingSystem?.pvProductionKwhPerYear;
      const existingSystemPvInstalledAt =
        calculationValues?.existingSystem?.pvInstalledAt;
      const existingSystemPvPeakPowerKwp =
        calculationValues?.existingSystem?.pvPeakPowerKwp;
      const existingSystemBatteryCapacityKwh =
        calculationValues?.existingSystem?.batteryCapacityKwh;
      const manual14aOverride = calculationValues?.manual14aOverride;
      const directMarketingEnabled = calculationValues?.directMarketingEnabled;

      utils.configuration.getById.setData(
        { configurationHashId, userId: user?.id },
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          if (
            !currentData.metrics.effectivePriceData ||
            !currentData.metrics.effectivePriceData?.germany
          ) {
            return currentData;
          }

          return {
            ...currentData,
            metrics: {
              ...currentData.metrics,
              effectivePriceData: {
                ...currentData.metrics.effectivePriceData,
                germany: {
                  ...currentData.metrics.effectivePriceData?.germany,
                  ...(spotPriceEurPerKwh !== undefined
                    ? {
                        spotPriceEurPerKwh: {
                          ...currentData.metrics.effectivePriceData?.germany
                            .spotPriceEurPerKwh,
                          override: spotPriceEurPerKwh,
                        },
                      }
                    : {}),
                  ...(manual14aOverride !== undefined
                    ? {
                        manual14aOverride,
                        isEligibleForParagraph14aSavings:
                          manual14aOverride === true,
                      }
                    : {}),
                  ...(directMarketingEnabled !== undefined
                    ? { directMarketingEnabled }
                    : {}),
                  existingSystem: {
                    ...currentData.metrics.effectivePriceData?.germany
                      .existingSystem,
                    ...(existingSystemPvProductionKwhPerYear !== undefined
                      ? {
                          pvProductionKwhPerYear:
                            existingSystemPvProductionKwhPerYear,
                        }
                      : {}),
                    ...(existingSystemPvInstalledAt !== undefined
                      ? {
                          pvInstalledAt: existingSystemPvInstalledAt,
                        }
                      : {}),
                    ...(existingSystemPvPeakPowerKwp !== undefined
                      ? {
                          pvPeakPowerKwp: existingSystemPvPeakPowerKwp,
                        }
                      : {}),
                    ...(existingSystemBatteryCapacityKwh !== undefined
                      ? {
                          batteryCapacityKwh: existingSystemBatteryCapacityKwh,
                        }
                      : {}),
                  },
                  ...(spotPriceEurPerKwh !== undefined
                    ? {
                        fixedTariffGridCostsMinorCurrencyUnitPerKwh:
                          spotPriceEurPerKwh === null
                            ? null
                            : spotPriceEurPerKwh * 100,
                      }
                    : {}),
                  consumption: {
                    ...currentData.metrics.effectivePriceData?.germany
                      .consumption,
                    household: {
                      ...currentData.metrics.effectivePriceData?.germany
                        .consumption.household,
                      amountKwhPerYear:
                        householdKwh ??
                        currentData.metrics.effectivePriceData?.germany
                          .consumption.household.amountKwhPerYear,
                    },
                    heat: {
                      ...currentData.metrics.effectivePriceData?.germany
                        .consumption.heat,
                      amountKwhPerYear:
                        calculationValues?.consumption?.heat === null
                          ? 0
                          : (heatKwh ??
                            currentData.metrics.effectivePriceData?.germany
                              .consumption.heat.amountKwhPerYear),
                    },
                    mobility: {
                      ...currentData.metrics.effectivePriceData?.germany
                        .consumption.mobility,
                      ...(calculationValues?.consumption?.mobility === null
                        ? {
                            amountKwhPerYear: 0,
                            amountKmPerYear: 0,
                            kwhPer100km: 0,
                          }
                        : {}),
                    },
                  },
                  fossilConsumption: {
                    heat:
                      calculationValues?.fossilConsumption?.heat === null
                        ? null
                        : currentData.metrics.effectivePriceData?.germany
                            .fossilConsumption?.heat,
                    mobility:
                      calculationValues?.fossilConsumption?.mobility === null
                        ? null
                        : currentData.metrics.effectivePriceData?.germany
                            .fossilConsumption?.mobility,
                  },
                },
              },
            },
          };
        },
      );
    },

    onSettled: (_data, _error, { configurationId: configurationHashId }) => {
      void utils.configuration.getById.invalidate({
        configurationHashId,
        userId: user?.id,
      });
    },
  });

  const finaliseConfig = api.configuration.finaliseConfig.useMutation({
    onSuccess: (_data, variables) => {
      void utils.configuration.getById.invalidate({
        configurationHashId: variables.configurationId,
      });
      router.refresh();
    },
  });

  const sendOffer = api.configuration.sendOffer.useMutation({
    onSuccess: (_data, variables) => {
      void utils.configuration.getById.invalidate({
        configurationHashId: variables.configurationId,
      });
      router.refresh();
    },
  });

  const updateProducts = api.configuration.updateProducts.useMutation({
    onMutate: ({ configurationId, products }) => {
      utils.configuration.getById.setData(
        { configurationHashId: configurationId },
        (currentData) =>
          currentData
            ? {
                ...currentData,
                products: updateConfigurationProductsLocally(
                  currentData.products,
                  products,
                ),
              }
            : undefined,
      );
    },
    onSuccess: (data, variables) => {
      utils.configuration.getById.setData(
        { configurationHashId: variables.configurationId },
        data,
      );
      void utils.configuration.getById.invalidate({
        configurationHashId: variables.configurationId,
      });
      void utils.configuration.getAssistantTopics.invalidate({
        configurationId: variables.configurationId,
      });
      router.refresh();
    },
  });
  const updateAbsoluteDiscount =
    api.configuration.updateAbsoluteDiscount.useMutation({
      onSuccess: (_data, variables) => {
        void utils.configuration.getById.invalidate({
          configurationHashId: variables.configurationId,
        });
        void utils.configuration.getAssistantTopics.invalidate({
          configurationId: variables.configurationId,
        });
        router.refresh();
      },
    });
  const isSavingAbsoluteDiscount =
    useIsMutating({
      mutationKey: getMutationKey(api.configuration.updateAbsoluteDiscount),
    }) > 0;

  const updateAbsoluteSubsidy =
    api.configuration.updateAbsoluteSubsidy.useMutation({
      onSuccess: (_data, variables) => {
        void utils.configuration.getById.invalidate({
          configurationHashId: variables.configurationId,
        });
        router.refresh();
      },
    });

  const lookupEnergyServiceData =
    api.configuration.lookupEnergyServiceData.useMutation({
      onSuccess: (_data, variables) => {
        void utils.configuration.getById.invalidate({
          configurationHashId: variables.configurationId,
        });
        router.refresh();
      },
    });

  const removeProductsByType =
    api.configuration.removeProductsByType.useMutation({
      onSuccess: (_data, variables) => {
        void utils.configuration.getById.invalidate({
          configurationHashId: variables.configurationId,
        });
        router.refresh();
      },
    });

  return {
    getById,
    getAssistantTopics,
    deleteConfiguration,
    duplicateConfiguration,
    addConceptProductsToConfiguration,
    updateConfig,
    updateConfigWithoutInvalidation,
    updateConfigOptimistic,
    finaliseConfig,
    sendOffer,
    previewConfig,
    updateProducts,
    lookupEnergyServiceData,
    removeProductsByType,
    updateAbsoluteDiscount,
    isSavingAbsoluteDiscount,
    updateAbsoluteSubsidy,
  };
};
